import { useMemo, useState } from 'react';

import { InputActionMeta, MultiValue, OnChangeValue } from '@any-ui-react/select';

import { BasicOption } from '@anyx/shared-utils';

type CustomerTagOption = BasicOption<string>;

const createOption = (label: string): CustomerTagOption => ({
  label,
  value: label,
});

const initialState = {
  tagSelections: (tags: string[]) =>
    tags.map((tag) => {
      return createOption(tag);
    }),
  inputValue: '',
};

export const useCreateCustomerTagInput = (tags: string[] = [], initInputValue = '') => {
  const [tagSelections, setTagSelections] = useState<MultiValue<CustomerTagOption>>(
    initialState.tagSelections(tags)
  );

  const [inputValue, setInputValue] = useState(initInputValue);

  const handleTagsChange = (values: OnChangeValue<CustomerTagOption, true>) => {
    setTagSelections(values);
  };

  const addToSelection = (tag: string) => {
    // only add to selection if it's not already in the selection
    const trimmedTag = tag.trim();
    if (tagSelections.every((v) => v.value !== trimmedTag)) {
      handleTagsChange([...tagSelections, createOption(trimmedTag)]);
      setInputValue(initInputValue || initialState.inputValue);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (!inputValue) return;
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        event.stopPropagation();
        setInputValue(initInputValue || initialState.inputValue);
        addToSelection(inputValue);
    }
  };

  const handleInputChange = (newInputValue: string, { action }: InputActionMeta) => {
    if (action === 'input-change') {
      setInputValue(newInputValue);
    }
  };

  const resetValues = () => {
    setTagSelections(initialState.tagSelections(tags || []));
    setInputValue(initInputValue || initialState.inputValue);
  };

  const stringTagSelection: string[] = useMemo(
    () => tagSelections.map((tag) => tag.value),
    [tagSelections]
  );

  return {
    addToSelection,
    handleTagsChange,
    handleKeyDown,
    handleInputChange,
    inputValue,
    tagSelections,
    stringTagSelection,
    resetValues,
  };
};
